import React from 'react';
import { Building2, Edit2, Gauge } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../../components/ui/tabs';
import ClientEmailLog from './ClientEmailLog';
import { OverviewTab } from './detail/OverviewTab';
import { TermsTab } from './detail/TermsTab';
import { LocationsTab } from './detail/LocationsTab';
import { DocumentsTab } from './detail/DocumentsTab';
import { NotesTab } from './detail/NotesTab';
import { HistoryTab } from './detail/HistoryTab';
import { UsersTab } from './detail/UsersTab';
import { RecurringTab } from './detail/RecurringTab';

export const ClientDetailDialog = ({ detail, initialTab = 'overview' }) => {
  const {
    detailOpen, setDetailOpen, selectedClient, clientDetail, loadingDetail,
    editMode, setEditMode, editForm, setEditForm, savingChanges,
    newNote, setNewNote, editingLocation, setEditingLocation,
    auditLog, auditPage, loadingAudit, clientDocs, loadingDocs,
    loadAuditLog, handleSavePreferences, handleAddNote, handleDeleteNote,
    handleSaveLocation, handleDeleteLocation,
  } = detail;

  const onCancelEdit = () => setEditMode(false);

  return (
    <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {selectedClient && (
          <>
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-amber-500" />
                  {selectedClient.company_name}
                </DialogTitle>
                <DialogDescription className="sr-only"> dialog. Use the form below to continue.</DialogDescription>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                    data-testid="client-performance-link"
                  >
                    <Link
                      to={`/admin/reports/client-performance?tab=scorecards&client=${selectedClient.id}`}
                    >
                      <Gauge className="h-4 w-4 mr-2" />
                      Performance
                    </Link>
                  </Button>
                  {!editMode && (
                    <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                      <Edit2 className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  )}
                </div>
              </div>
            </DialogHeader>

            {loadingDetail ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
              </div>
            ) : clientDetail ? (
              <Tabs defaultValue={initialTab} className="mt-4">
                <TabsList className="grid grid-cols-9 w-full">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="terms">Terms & Invoicing</TabsTrigger>
                  <TabsTrigger value="locations">Locations</TabsTrigger>
                  <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
                  <TabsTrigger value="recurring" data-testid="tab-recurring">Recurring</TabsTrigger>
                  <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
                  <TabsTrigger value="notes">Notes</TabsTrigger>
                  <TabsTrigger value="history">History</TabsTrigger>
                  <TabsTrigger value="email-log" data-testid="tab-email-log">Notif Log</TabsTrigger>
                </TabsList>

                <TabsContent value="overview">
                  <OverviewTab
                    clientDetail={clientDetail}
                    editMode={editMode}
                    editForm={editForm}
                    setEditForm={setEditForm}
                    savingChanges={savingChanges}
                    onSave={handleSavePreferences}
                    onCancelEdit={onCancelEdit}
                  />
                </TabsContent>

                <TabsContent value="terms">
                  <TermsTab
                    clientDetail={clientDetail}
                    editMode={editMode}
                    editForm={editForm}
                    setEditForm={setEditForm}
                    savingChanges={savingChanges}
                    onSave={handleSavePreferences}
                    onCancelEdit={onCancelEdit}
                  />
                </TabsContent>

                <TabsContent value="locations">
                  <LocationsTab
                    clientDetail={clientDetail}
                    editingLocation={editingLocation}
                    setEditingLocation={setEditingLocation}
                    onSaveLocation={handleSaveLocation}
                    onDeleteLocation={handleDeleteLocation}
                  />
                </TabsContent>

                <TabsContent value="documents" data-testid="documents-tab-content">
                  <DocumentsTab clientDocs={clientDocs} loadingDocs={loadingDocs} />
                </TabsContent>

                <TabsContent value="users">
                  <UsersTab clientId={clientDetail.id} />
                </TabsContent>

                <TabsContent value="recurring">
                  <RecurringTab clientDetail={clientDetail} />
                </TabsContent>

                <TabsContent value="notes">
                  <NotesTab
                    clientDetail={clientDetail}
                    newNote={newNote}
                    setNewNote={setNewNote}
                    onAddNote={handleAddNote}
                    onDeleteNote={handleDeleteNote}
                  />
                </TabsContent>

                <TabsContent value="history">
                  <HistoryTab
                    clientDetail={clientDetail}
                    auditLog={auditLog}
                    auditPage={auditPage}
                    loadingAudit={loadingAudit}
                    onPageChange={(page) => loadAuditLog(clientDetail.id, page)}
                  />
                </TabsContent>

                <TabsContent value="email-log" className="mt-4">
                  <ClientEmailLog clientId={clientDetail.id} />
                </TabsContent>
              </Tabs>
            ) : null}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
